/**
 * One AI call campaign (`/ai-calls/campaigns/:id`, and `/new`): whom it may
 * ring, how it opens, and its queue (ring R3), and how the automation works it
 * (ring R4).
 *
 * The page is the wiring: the basics, the audience, the opening and offer
 * (the settings' own editor, with the settings' texts as what a blank field
 * means), and beside them the preview of who the audience would ring right now,
 * the automation's fields with "call the next one now", and the queue. Under
 * them, once the campaign exists, the call flow map the AI follows (ring R8).
 * Every save sends only what changed; the server checks the opening the texts
 * make and answers the field that broke it, refuses to activate a campaign
 * whose map cannot be followed, and refuses dates that end before they start.
 *
 * An alert campaign (ring R9) swaps the opening editor for `AlertOpeningCard`:
 * the sentence about the defect is code, only the question is the owner's, and
 * there is no pitch.
 *
 * Read-only without `manage:crm.calls`: an operator may look at a campaign,
 * only a manager decides whom a machine rings.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Loader2, Save, Trash2 } from "lucide-react";
import { ConfirmDialog } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { ApiError } from "../../../../lib/api";
import { userCanPlatform, type StaffUser } from "../../../../lib/auth";
import { getAiOutboundStatus, type AiOutboundStatus } from "../../../../lib/aiOutbound";
import { campaignLoadFailure } from "../../../../lib/aiCampaignPage";
import {
  campaignPreset,
  createAiCampaign,
  deleteAiCampaign,
  emptyCampaignDraft,
  flowCardAngles,
  getAiCampaign,
  patchAiCampaign,
  toCampaignBody,
  toCampaignDraft,
  toCampaignPatch,
  withPreset,
  type AiCallCampaign,
  type CampaignDraft,
  type CampaignKind,
} from "../../../../lib/aiCampaigns";
import { Btn, EmptyState, PageHead, StatusPill } from "../../../ui";
import { Note } from "../../settings/panel/fields";
import { AiOutboundOpening } from "../../settings/phone/aiOutbound/AiOutboundOpening";
import { AudiencePreviewCard } from "./AudiencePreviewCard";
import { CampaignAudienceCard } from "./CampaignAudienceCard";
import { CampaignAutomationCard } from "./CampaignAutomationCard";
import { CampaignBasicsCard } from "./CampaignBasicsCard";
import { CampaignQueueCard } from "./CampaignQueueCard";
import { campaignStatusTone } from "./campaignTone";
import { AlertOpeningCard } from "./alert/AlertOpeningCard";
import { CampaignFlowCard } from "./flow";

const BLANK_REASONS = { letter: "", call: "", cold: "" };

/** The error key a failed save is shown with. */
function saveErrorKey(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.message === "opening_invalid") return "opening_invalid";
    if (err.message === "flow_invalid") return "flow_invalid";
    if (err.message === "invalid_body") return "invalid_body";
    if (err.message === "alert_includes_clients") return "alert_includes_clients";
    if (err.message === "bad_dates") return "bad_dates";
    if (err.status === 403) return "forbidden";
  }
  return "failed";
}

export function CampaignEditor({ user, isNew = false }: { user: StaffUser; isNew?: boolean }) {
  const t = useT();
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const canManage = userCanPlatform(user, "manage:crm.calls");

  const [saved, setSaved] = useState<AiCallCampaign | null>(null);
  const [draft, setDraft] = useState<CampaignDraft | null>(isNew ? emptyCampaignDraft() : null);
  const [status, setStatus] = useState<AiOutboundStatus | null>(null);
  const [notFound, setNotFound] = useState(false);
  // A read that failed for another reason than a missing campaign: the page stays.
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Bumped when an audience arrives from outside the form, so its typing buffers reseed.
  const [resetKey, setResetKey] = useState("0");
  // Bumped after "call the next one now", so the queue card reads its rows again.
  const [queueKey, setQueueKey] = useState(0);

  const load = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await getAiCampaign(id);
      setSaved(res.campaign);
      setDraft((current) => current ?? toCampaignDraft(res.campaign));
      setLoadFailed(false);
    } catch (err) {
      // Only a 404 is a missing campaign. A reload right after "call the next one
      // now" that failed for a moment must not swap the page, and with it the
      // note saying a call started, for "campaign not found".
      if (campaignLoadFailure(err) === "not_found") setNotFound(true);
      else setLoadFailed(true);
    }
  }, [id, isNew]);

  useEffect(() => {
    void load();
  }, [load]);

  // The settings' texts are what a blank campaign text means. Only a manager
  // may read them; without them the built-in sentences stand in.
  useEffect(() => {
    if (!canManage) return;
    getAiOutboundStatus()
      .then(setStatus)
      .catch(() => {
        /* the editor works on the built-in sentences */
      });
  }, [canManage]);

  const patch = useMemo(() => (draft && saved ? toCampaignPatch(draft, saved) : null), [draft, saved]);
  const body = useMemo(() => (draft ? toCampaignBody(draft) : null), [draft]);
  const dirty = isNew ? true : patch !== null && Object.keys(patch).length > 0;
  const valid = isNew ? body !== null : patch !== null;

  function applyPreset(kind: CampaignKind) {
    if (!draft) return;
    // A preset picks whom to ring; on an alert campaign the goal stays the defect report.
    setDraft(withPreset(draft, campaignPreset(kind)));
    setResetKey((k) => String(Number(k) + 1));
  }

  async function save() {
    if (!draft || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      if (isNew) {
        if (!body) return;
        const res = await createAiCampaign(body);
        navigate(`/ai-calls/campaigns/${res.campaign.id}`, { replace: true });
        return;
      }
      if (!patch || Object.keys(patch).length === 0) return;
      const res = await patchAiCampaign(id, patch);
      setSaved(res.campaign);
      setDraft(toCampaignDraft(res.campaign));
    } catch (err) {
      setSaveError(saveErrorKey(err));
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    setConfirmDelete(false);
    setDeleteError(null);
    try {
      await deleteAiCampaign(id);
      navigate("/ai-calls/campaigns", { replace: true });
    } catch (err) {
      setDeleteError(err instanceof ApiError && err.message === "has_calls" ? "has_calls" : "failed");
    }
  }

  if (notFound) {
    return (
      <EmptyState
        icon={ArrowLeft}
        title={t("aiCalls.campaigns.notFound")}
        action={<Btn onClick={() => navigate("/ai-calls/campaigns")}>{t("aiCalls.campaigns.back")}</Btn>}
      />
    );
  }
  if (!draft && loadFailed) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title={t("aiCalls.campaigns.loadFailed")}
        body={t("aiCalls.campaigns.loadFailedBody")}
        action={<Btn onClick={() => void load()}>{t("aiCalls.campaigns.reload")}</Btn>}
      />
    );
  }
  if (!draft) {
    return (
      <div className="flex justify-center py-16 text-muted-foreground">
        <Loader2 className="size-6 animate-spin" />
      </div>
    );
  }

  const settingsTexts = status
    ? { reasons: { ...BLANK_REASONS, ...status.settings.openingReasons }, question: status.settings.openingQuestion ?? "" }
    : { reasons: BLANK_REASONS, question: "" };
  const builtIn = status?.openingDefaults ?? { question: "", reasons: BLANK_REASONS };
  const placeholders = {
    question: settingsTexts.question || builtIn.question,
    reasons: {
      letter: settingsTexts.reasons.letter || builtIn.reasons.letter,
      call: settingsTexts.reasons.call || builtIn.reasons.call,
      cold: settingsTexts.reasons.cold || builtIn.reasons.cold,
    },
  };
  const disabled = !canManage;

  return (
    <div>
      <PageHead
        title={isNew ? t("aiCalls.campaigns.newTitle") : saved?.name || t("aiCalls.campaigns.untitled")}
        sub={
          saved ? (
            <StatusPill tone={campaignStatusTone(saved.status)}>{t(`aiCalls.campaigns.status.${saved.status}`)}</StatusPill>
          ) : (
            t("aiCalls.campaigns.newIntro")
          )
        }
        actions={
          <>
            <Btn onClick={() => navigate("/ai-calls/campaigns")}>
              <ArrowLeft className="size-3.5" />
              {t("aiCalls.campaigns.back")}
            </Btn>
            {canManage && !isNew ? (
              <Btn onClick={() => setConfirmDelete(true)}>
                <Trash2 className="size-3.5" />
                {t("common.delete")}
              </Btn>
            ) : null}
            {canManage ? (
              <Btn tone="dark" onClick={() => void save()} disabled={saving || !dirty || !valid}>
                {saving ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
                {isNew ? t("aiCalls.campaigns.create") : t("aiCalls.campaigns.save")}
              </Btn>
            ) : null}
          </>
        }
      />

      {loadFailed ? (
        <Note tone="warn" live className="mb-4">
          {t("aiCalls.campaigns.loadStale")}
        </Note>
      ) : null}
      {saveError ? (
        <Note tone="bad" live className="mb-4">
          {t(`aiCalls.campaigns.saveError.${saveError}`)}
        </Note>
      ) : null}
      {deleteError ? (
        <Note tone="bad" live className="mb-4">
          {t(`aiCalls.campaigns.deleteError.${deleteError}`)}
        </Note>
      ) : null}
      {disabled ? (
        <Note tone="info" className="mb-4">
          {t("aiCalls.campaigns.readOnly")}
        </Note>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)]">
        <div className="min-w-0 space-y-5">
          <CampaignBasicsCard draft={draft} onChange={setDraft} isNew={isNew} disabled={disabled} onPreset={applyPreset} />
          <CampaignAudienceCard
            audience={draft.audience}
            onChange={(audience) => setDraft({ ...draft, audience })}
            disabled={disabled}
            resetKey={`${id}:${resetKey}`}
            alert={draft.angle === "alert"}
          />
          {draft.angle === "alert" ? (
            <AlertOpeningCard
              question={draft.openingQuestion}
              onChange={(openingQuestion) => setDraft({ ...draft, openingQuestion })}
              disabled={disabled}
            />
          ) : (
            <section className="crm-card px-[26px] pb-6 pt-5">
              <AiOutboundOpening
                draft={{ reasons: draft.openingReasons, question: draft.openingQuestion, pitch: draft.pitch, goal: draft.goal }}
                onChange={(next) =>
                  setDraft({ ...draft, openingReasons: next.reasons, openingQuestion: next.question, pitch: next.pitch, goal: next.goal })
                }
                defaults={placeholders}
                fallback={settingsTexts}
                disabled={disabled}
                mode="campaign"
              />
            </section>
          )}
        </div>

        <div className="min-w-0 space-y-5">
          <AudiencePreviewCard
            audience={draft.audience}
            kind={draft.kind}
            angle={draft.angle}
            campaignId={saved?.id}
            enabled={canManage}
          />
          <CampaignAutomationCard
            draft={draft}
            onChange={setDraft}
            disabled={disabled}
            campaignId={saved?.id ?? null}
            canManage={canManage}
            // An edit that cannot be saved yet is still an unsaved edit: the
            // button must not ring on the stored terms while the page shows others.
            dirty={dirty || !valid}
            settingsZone={status?.settings.timezone ?? null}
            onCalled={() => {
              setQueueKey((k) => k + 1);
              void load();
            }}
          />
          {saved ? (
            <CampaignQueueCard campaign={saved} canManage={canManage} onFilled={() => void load()} reloadKey={queueKey} />
          ) : null}
        </div>
      </div>

      {saved ? (
        <div className="mt-5">
          <CampaignFlowCard
            campaignId={saved.id}
            canManage={canManage}
            active={saved.status === "active"}
            {...flowCardAngles(draft, saved)}
          />
        </div>
      ) : null}

      <ConfirmDialog
        open={confirmDelete}
        title={t("aiCalls.campaigns.deleteTitle")}
        description={t("aiCalls.campaigns.deleteConfirm")}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        variant="destructive"
        onConfirm={() => void remove()}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
